import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    Box,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Tooltip,
    IconButton,
    Button,
    Chip,
    Paper,
    Divider,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    styled,
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Menu,
    MenuItem,
    Fab,
    Grid
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiMap, mdiPlus, mdiMinus, mdiTruckFast, mdiPentagon, mdiDomain,
    mdiAlertBoxOutline, mdiBookMarker, mdiAlertBox, mdiRoadVariant,
    mdiMapMarkerPath, mdiCounter, mdiTruckCheck, mdiShareVariantOutline,
    mdiCloseCircleOutline, mdiGasStation, mdiMapMarkerQuestion,
    mdiCog, mdiEye, mdiEyeOff, mdiDotsVertical, mdiChevronDown, mdiChevronUp,
    mdiLayers, mdiViewGrid, mdiPlusCircle
} from '@mdi/js';
import { toast } from "react-toastify";

import {
    detailViewSelector,
    clearVisibleAssetsMarkers,
    restoreVisibleAssetsMarkers,
    removeDetailView,
    selectVehicleById
} from '../../features/assets/assetsSlice';
import {
    clearVisibleMarkersMap, clearVisibleZonesMap,
    markerAutoLoadSelector, zoneAutoLoadSelector
} from '../../features/places/placesSlice';
import { history24 } from '../../features/datacenter/historySlice';
import {
    zoomIn, zoomOut, setNeedBounding,
    changeGoogleMapTypeId, toggleVehicleIcons, toggleZonePolygons,
    togglePlaceMarkers, showVehicleIconsSelector,
    showZonePolygonsSelector, showPlaceMarkersSelector,
    mapServiceSelector, changeMapService, googleAvailableSelector,
    mapControlSelector, tripLayersSelector, toggleTripLayer,
} from '../../features/map/mapSlice';
import { selectedTripSelector } from '../../features/tripcenter/tripControlSlice';
import { setViewType, toggleEventTicker } from '../../features/events/eventsSlice';
import { userSelector } from '../../features/user/UserSlice';

import MapTypeModal from './MapTypeModal';
import AddressModal from '../assetView/AddressModal';
import AssetKilometersModal from '../assetView/AssetKilometersModal';
import ChangeOdometerModal from '../assetView/ChangeOdometerModal';
import ChangeIconModal from '../assetView/ChangeIconModal';
import ShareLinkModal from '../assetView/ShareLinkModal';
import FuelMainModal from '../fuelAdmin/FuelMainModal';
import FindLatLng from './FindLatLng';
import ZoneTagFilterModal from './ZoneTagFilterModal';
import MarkerTagFilterModal from './MarkerTagFilterModal';
import NewTrip from '../tripcenter/tripComponents/NewTrip';

// Styled Components
const ToolbarContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: theme.spacing(10), // Account for header height
    right: theme.spacing(2),
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '210px',
    maxWidth: `calc(100vw - ${theme.spacing(4)})`,
    maxHeight: 'calc(100vh - 120px)', // Prevent overflow
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        top: theme.spacing(8),
        right: theme.spacing(1),
        left: theme.spacing(1),
        width: 'auto',
        maxWidth: 'none',
        flexDirection: 'row',
        justifyContent: 'center',
        maxHeight: 'auto',
    }
}));

const CompactButton = styled(Button)(({ theme, active, variant = 'default' }) => ({
    minWidth: 'auto',
    padding: theme.spacing(1, 1.5),
    fontSize: '0.75rem',
    textTransform: 'none',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: active
        ? theme.palette.primary.main
        : theme.palette.background.paper,
    color: active
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: active
            ? theme.palette.primary.dark
            : theme.palette.action.hover,
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[2],
    },
    '&:active': {
        transform: 'translateY(0)',
    },
    ...(variant === 'danger' && {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        }
    })
}));

const SectionHeader = styled(Box)(({ theme, sectionType = 'default' }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'none',
        boxShadow: 'none',
    },
    '& .section-icon': {
        transition: 'all 0.2s ease-in-out',
    },
    '&:hover .section-icon': {
        transform: 'scale(1.05)',
    },
    ...(sectionType === 'map' && {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: theme.palette.primary[50],
        }
    }),
    ...(sectionType === 'layers' && {
        borderLeft: `4px solid ${theme.palette.success.main}`,
        '&:hover': {
            backgroundColor: theme.palette.success[50],
        }
    }),
    ...(sectionType === 'asset' && {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        '&:hover': {
            backgroundColor: theme.palette.warning[50],
        }
    })
}));

const ActionList = styled(List)(({ theme }) => ({
    padding: theme.spacing(0.5),
    '& .MuiListItem-root': {
        padding: theme.spacing(0.25),
    },
    '& .MuiListItemButton-root': {
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5, 1),
        fontSize: '0.75rem',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        }
    }
}));

const GridContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(0.5),
    maxHeight: '400px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.grey[100],
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[400],
        borderRadius: '3px',
        '&:hover': {
            background: theme.palette.grey[500],
        }
    }
}));

const GridButton = styled(Button)(({ theme, variant = 'default', active }) => ({
    minWidth: 'auto',
    padding: theme.spacing(1, 1.5),
    fontSize: '0.75rem',
    textTransform: 'none',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    border: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: 'auto',
    minHeight: '70px',
    position: 'relative',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'none',
        boxShadow: 'none',
    },
    '&:active': {
        transform: 'scale(0.98)',
    },
    '& .MuiButton-startIcon': {
        margin: 0,
        marginBottom: theme.spacing(0.25),
    },
    // Asset Tools Color Variants - Modern flat design
    ...(variant === 'danger' && {
        color: '#dc3545',
        '&:hover': {
            backgroundColor: '#fff5f5',
            color: '#c82333',
        }
    }),
    ...(variant === 'primary' && {
        color: '#2563eb',
        '&:hover': {
            backgroundColor: '#eff6ff',
            color: '#1d4ed8',
        }
    }),
    ...(variant === 'secondary' && {
        color: '#7c3aed',
        '&:hover': {
            backgroundColor: '#faf5ff',
            color: '#6d28d9',
        }
    }),
    ...(variant === 'info' && {
        color: '#0891b2',
        '&:hover': {
            backgroundColor: '#f0fdfa',
            color: '#0e7490',
        }
    }),
    ...(variant === 'warning' && {
        color: '#ea580c',
        '&:hover': {
            backgroundColor: '#fff7ed',
            color: '#c2410c',
        }
    }),
    // Layer Tools Color Variants - Modern flat design with active states
    ...(variant === 'vehicles' && {
        color: active ? '#059669' : '#3b82f6',
        backgroundColor: active ? '#ecfdf5' : 'transparent',
        '&:hover': {
            backgroundColor: active ? '#d1fae5' : '#eff6ff',
            color: active ? '#047857' : '#2563eb',
        }
    }),
    ...(variant === 'zones' && {
        color: active ? '#059669' : '#8b5cf6',
        backgroundColor: active ? '#ecfdf5' : 'transparent',
        '&:hover': {
            backgroundColor: active ? '#d1fae5' : '#faf5ff',
            color: active ? '#047857' : '#7c3aed',
        }
    }),
    ...(variant === 'markers' && {
        color: active ? '#059669' : '#f59e0b',
        backgroundColor: active ? '#ecfdf5' : 'transparent',
        '&:hover': {
            backgroundColor: active ? '#d1fae5' : '#fffbeb',
            color: active ? '#047857' : '#d97706',
        }
    }),
    ...(variant === 'ticker' && {
        color: active ? '#059669' : '#06b6d4',
        backgroundColor: active ? '#ecfdf5' : 'transparent',
        '&:hover': {
            backgroundColor: active ? '#d1fae5' : '#ecfeff',
            color: active ? '#047857' : '#0891b2',
        }
    })
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1001,
    [theme.breakpoints.down('sm')]: {
        bottom: theme.spacing(1),
        right: theme.spacing(1),
    }
}));

const MapToolbar = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Redux selectors
    const user = useSelector(userSelector);
    const focusedAsset = useSelector(detailViewSelector);
    const focusedVehicle = useSelector(state => focusedAsset ? selectVehicleById(state, focusedAsset) : null);
    const markerAutoLoad = useSelector(markerAutoLoadSelector);
    const zoneAutoLoad = useSelector(zoneAutoLoadSelector);
    const eventTicker = useSelector(state => state.events.eventTicker);
    const showVehicleIcons = useSelector(showVehicleIconsSelector);
    const showZonePolygons = useSelector(showZonePolygonsSelector);
    const showPlaceMarkers = useSelector(showPlaceMarkersSelector);
    const mapService = useSelector(mapServiceSelector);
    const googleAvailable = useSelector(googleAvailableSelector);
    const mapControl = useSelector(mapControlSelector);
    const selectedTrip = useSelector(selectedTripSelector);
    const tripLayers = useSelector(tripLayersSelector);
    const isTripViewActive = mapControl === 'trip' && Boolean(selectedTrip);

    // Local state
    const [firstLoad, setFirstLoad] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        map: true, // Keep map controls expanded by default
        layers: false,
        asset: false,
        trip: false,
    });
    const [mapTypeOpen, setMapTypeOpen] = useState(false);
    const [addressOpen, setAddressOpen] = useState(false);
    const [distanceOpen, setDistanceOpen] = useState(false);
    const [changeOdometerOpen, setChangeOdometerOpen] = useState(false);
    const [changeIconOpen, setChangeIconOpen] = useState(false);
    const [shareLinkOpen, setShareLinkOpen] = useState(false);
    const [fuelSetupOpen, setFuelSetupOpen] = useState(false);
    const [findLatLngOpen, setFindLatLngOpen] = useState(false);
    const [zoneTagFilterOpen, setZoneTagFilterOpen] = useState(false);
    const [markerTagFilterOpen, setMarkerTagFilterOpen] = useState(false);
    const [quickMenuAnchor, setQuickMenuAnchor] = useState(null);
    const [newTripOpen, setNewTripOpen] = useState(false);

    // Initialize auto-load settings
    useEffect(() => {
        if (!firstLoad) {
            if (zoneAutoLoad !== showZonePolygons) {
                dispatch(toggleZonePolygons());
            }
            if (markerAutoLoad !== showPlaceMarkers) {
                dispatch(togglePlaceMarkers());
            }
            setFirstLoad(true);
        }
    }, [dispatch, firstLoad, zoneAutoLoad, markerAutoLoad, showZonePolygons, showPlaceMarkers]);

    // Auto-expand asset tools panel when asset is focused
    useEffect(() => {
        console.log("map toolbar: Changed focused asset", focusedAsset)
        if (focusedAsset) {
            setExpandedSections(prev => ({
                map: prev.map, // Keep map controls state
                layers: false, // Collapse layers panel
                asset: true    // Open asset tools panel
            }));
        }
    }, [focusedAsset]);

    useEffect(() => {
        if (isTripViewActive) {
            setExpandedSections({ map: false, layers: false, asset: false, trip: true });
        }
    }, [isTripViewActive, selectedTrip?._id]);

    // Action handlers
    const handleMapTypeChange = (value) => {
        setMapTypeOpen(false);
        if (value) {
            if (value === 'osm') {
                if (mapService !== 'osm') {
                    dispatch(changeMapService('osm'));
                    history.push('/secured/livetracking');
                }
            } else {
                if (googleAvailable) {
                    dispatch(changeGoogleMapTypeId(value));
                    if (mapService !== 'google') {
                        dispatch(changeMapService('google'));
                        history.push('/secured/livetracking');
                    }
                } else {
                    toast.error("Google Map is not available.");
                }
            }
        }
    };

    const handleVehicleToggle = () => {
        if (showVehicleIcons) {
            dispatch(clearVisibleAssetsMarkers());
        } else {
            dispatch(restoreVisibleAssetsMarkers());
        }
        dispatch(toggleVehicleIcons());
        dispatch(setNeedBounding(true));
    };

    const handleZoneToggle = () => {
        if (showZonePolygons) {
            dispatch(clearVisibleZonesMap());
            dispatch(toggleZonePolygons());
        } else {
            setZoneTagFilterOpen(true);
        }
    };

    const handleMarkerToggle = () => {
        if (showPlaceMarkers) {
            dispatch(clearVisibleMarkersMap());
            dispatch(togglePlaceMarkers());
        } else {
            setMarkerTagFilterOpen(true);
        }
    };

    const handleAssetDetailClose = () => {
        dispatch(removeDetailView());
        dispatch(setViewType('default'));
    };

    const handleGetHistory = () => {
        dispatch(history24({
            assetId: focusedAsset,
            assetName: focusedVehicle ? focusedVehicle.vehicleDevice.regn : '',
        }));
    };

    const handleAssetEventModalOpen = () => {
        dispatch(setViewType('oneAsset'));
    };

    // Enhanced toggle section with mutual exclusivity
    const toggleSection = (section) => {
        setExpandedSections(prev => {
            const newState = { ...prev };

            // If clicking the same section, just toggle it
            if (newState[section]) {
                newState[section] = false;
            } else {
                // If clicking a different section, close others and open this one
                Object.keys(newState).forEach(key => {
                    newState[key] = key === section;
                });
            }

            return newState;
        });
    };

    // Quick actions for mobile/compact view
    const quickActions = useMemo(() => [
        {
            icon: mdiPlus,
            label: 'Zoom In',
            action: () => dispatch(zoomIn()),
            color: 'primary'
        },
        {
            icon: mdiMinus,
            label: 'Zoom Out',
            action: () => dispatch(zoomOut()),
            color: 'primary'
        },
        {
            icon: showVehicleIcons ? mdiEyeOff : mdiEye,
            label: showVehicleIcons ? 'Hide Vehicles' : 'Show Vehicles',
            action: handleVehicleToggle,
            color: showVehicleIcons ? 'success' : 'default'
        },
        {
            icon: showZonePolygons ? mdiEyeOff : mdiEye,
            label: showZonePolygons ? 'Hide Zones' : 'Show Zones',
            action: handleZoneToggle,
            color: showZonePolygons ? 'success' : 'default'
        },
        {
            icon: showPlaceMarkers ? mdiEyeOff : mdiEye,
            label: showPlaceMarkers ? 'Hide Markers' : 'Show Markers',
            action: handleMarkerToggle,
            color: showPlaceMarkers ? 'success' : 'default'
        },
        ...(isTripViewActive ? [
            {
                icon: tripLayers.truckPath ? mdiEyeOff : mdiEye,
                label: tripLayers.truckPath ? 'Hide Truck Trail' : 'Show Truck Trail',
                action: () => dispatch(toggleTripLayer('truckPath')),
                color: tripLayers.truckPath ? 'success' : 'default'
            },
            {
                icon: tripLayers.plannedRoute ? mdiEyeOff : mdiEye,
                label: tripLayers.plannedRoute ? 'Hide Planned Route' : 'Show Planned Route',
                action: () => dispatch(toggleTripLayer('plannedRoute')),
                color: tripLayers.plannedRoute ? 'success' : 'default'
            },
            {
                icon: tripLayers.routeProgress ? mdiEyeOff : mdiEye,
                label: tripLayers.routeProgress ? 'Hide Route Progress' : 'Show Route Progress',
                action: () => dispatch(toggleTripLayer('routeProgress')),
                color: tripLayers.routeProgress ? 'success' : 'default'
            },
            {
                icon: tripLayers.stops ? mdiEyeOff : mdiEye,
                label: tripLayers.stops ? 'Hide Stops' : 'Show Stops',
                action: () => dispatch(toggleTripLayer('stops')),
                color: tripLayers.stops ? 'success' : 'default'
            },
        ] : [])
    ], [showVehicleIcons, showZonePolygons, showPlaceMarkers, isTripViewActive, tripLayers, dispatch]);

    // Layer tools configuration with grid layout and color coding
    const layerTools = useMemo(() => [
        {
            icon: showVehicleIcons ? mdiEye : mdiEyeOff,
            label: showVehicleIcons ? 'Hide Vehicles' : 'Show Vehicles',
            action: handleVehicleToggle,
            variant: 'vehicles',
            active: showVehicleIcons,
            tooltip: showVehicleIcons ? 'Hide vehicles from map' : 'Show vehicles on map'
        },
        {
            icon: showZonePolygons ? mdiEye : mdiEyeOff,
            label: showZonePolygons ? 'Hide Zones' : 'Show Zones',
            action: handleZoneToggle,
            variant: 'zones',
            active: showZonePolygons,
            tooltip: showZonePolygons ? 'Hide zones from map' : 'Show zones on map'
        },
        {
            icon: showPlaceMarkers ? mdiEye : mdiEyeOff,
            label: showPlaceMarkers ? 'Hide Markers' : 'Show Markers',
            action: handleMarkerToggle,
            variant: 'markers',
            active: showPlaceMarkers,
            tooltip: showPlaceMarkers ? 'Hide markers from map' : 'Show markers on map'
        },
        {
            icon: mdiAlertBoxOutline,
            label: eventTicker ? 'Hide Ticker' : 'Show Ticker',
            action: () => dispatch(toggleEventTicker()),
            variant: 'ticker',
            active: eventTicker,
            tooltip: eventTicker ? 'Hide event ticker' : 'Show event ticker'
        }
    ], [showVehicleIcons, showZonePolygons, showPlaceMarkers, eventTicker, dispatch]);

    const tripLayerTools = useMemo(() => {
        if (!isTripViewActive) return [];

        const tools = [
            {
                key: 'truckPath',
                label: tripLayers.truckPath ? 'Hide Truck Trail' : 'Show Truck Trail',
                color: '#d95f02',
                tooltip: 'Actual truck GPS trail with direction arrows',
            },
            {
                key: 'plannedRoute',
                label: tripLayers.plannedRoute ? 'Hide Planned Route' : 'Show Planned Route',
                color: '#6b7280',
                tooltip: 'Full planned route',
            },
            {
                key: 'routeProgress',
                label: tripLayers.routeProgress ? 'Hide Route Progress' : 'Show Route Progress',
                color: '#228b22',
                tooltip: 'Planned route covered to the current progress point',
            },
            {
                key: 'stops',
                label: tripLayers.stops ? 'Hide Stops' : 'Show Stops',
                color: '#8b008b',
                tooltip: 'Trip halt markers',
            },
            {
                key: 'routePlaces',
                label: tripLayers.routePlaces ? 'Hide Route Places' : 'Show Route Places',
                color: '#2563eb',
                tooltip: 'Start, end, and configured stop locations',
            },
            {
                key: 'routeAnnotations',
                label: tripLayers.routeAnnotations ? 'Hide Route Annotations' : 'Show Route Annotations',
                color: '#7c3aed',
                tooltip: 'Route-specific zones and markers',
            },
        ];

        return tools.map((tool) => ({
            ...tool,
            active: tripLayers[tool.key],
            action: () => dispatch(toggleTripLayer(tool.key)),
        }));
    }, [dispatch, isTripViewActive, tripLayers]);

    // Asset tools configuration with enhanced color palette
    const assetTools = useMemo(() => {
        if (!focusedAsset) return [];

        const tools = [
            {
                icon: mdiBookMarker,
                label: 'Location',
                action: () => setAddressOpen(true),
                variant: 'primary',
                tooltip: 'View location details'
            },
            {
                icon: mdiRoadVariant,
                label: 'Distance',
                action: () => setDistanceOpen(true),
                variant: 'info',
                tooltip: 'View distance/kilometers'
            },
            {
                icon: mdiMapMarkerPath,
                label: '24H Track',
                action: handleGetHistory,
                variant: 'secondary',
                tooltip: 'View 24-hour track'
            }
        ];

        if (!user.isSubAccount) {
            tools.push(
                {
                    icon: mdiAlertBox,
                    label: '24H Events',
                    action: handleAssetEventModalOpen,
                    variant: 'warning',
                    tooltip: 'View 24-hour events'
                },
                {
                    icon: mdiPlusCircle,
                    label: 'Create Trip',
                    action: () => setNewTripOpen(true),
                    variant: 'info',
                    tooltip: 'Create a new trip for this vehicle'
                },
                {
                    icon: mdiCounter,
                    label: 'Odometer',
                    action: () => setChangeOdometerOpen(true),
                    variant: 'info',
                    tooltip: 'Change odometer reading'
                },
                {
                    icon: mdiTruckCheck,
                    label: 'Change Icon',
                    action: () => setChangeIconOpen(true),
                    variant: 'secondary',
                    tooltip: 'Change vehicle icon'
                },
                {
                    icon: mdiShareVariantOutline,
                    label: 'Share Link',
                    action: () => setShareLinkOpen(true),
                    variant: 'primary',
                    tooltip: 'Share vehicle link'
                }
            );
        }

        if (user.isAdmin) {
            tools.push({
                icon: mdiGasStation,
                label: 'Fuel Setup',
                action: () => setFuelSetupOpen(true),
                variant: 'warning',
                tooltip: 'Configure fuel settings'
            });
        }

        tools.push({
            icon: mdiCloseCircleOutline,
            label: 'Close',
            action: handleAssetDetailClose,
            variant: 'danger',
            tooltip: 'Close asset details'
        });

        return tools;
    }, [focusedAsset, user, handleGetHistory, handleAssetEventModalOpen, handleAssetDetailClose]);

    // Render compact toolbar for mobile
    if (isMobile) {
        return (
            <>
                <Box sx={{
                    position: 'fixed',
                    top: theme.spacing(8),
                    right: theme.spacing(1),
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}>
                    {/* Status indicators */}
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Chip
                            label="V"
                            size="small" 
                            color={showVehicleIcons ? 'success' : 'default'}
                            variant={showVehicleIcons ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Z"
                            size="small" 
                            color={showZonePolygons ? 'success' : 'default'}
                            variant={showZonePolygons ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="M"
                            size="small" 
                            color={showPlaceMarkers ? 'success' : 'default'}
                            variant={showPlaceMarkers ? 'filled' : 'outlined'}
                        />
                    </Box>

                    {/* Primary actions */}
                    <Paper elevation={3} sx={{ p: 0.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <CompactButton
                                onClick={() => dispatch(zoomIn())}
                                size="small"
                                startIcon={<Icon path={mdiPlus} size={0.8} />}
                            >
                                Zoom In
                            </CompactButton>
                            <CompactButton
                                onClick={() => dispatch(zoomOut())}
                                size="small"
                                startIcon={<Icon path={mdiMinus} size={0.8} />}
                            >
                                Zoom Out
                            </CompactButton>
                            <CompactButton
                                onClick={() => setMapTypeOpen(true)}
                                size="small"
                                startIcon={<Icon path={mdiMap} size={0.8} />}
                            >
                                Map Type
                            </CompactButton>
                        </Box>
                    </Paper>
                </Box>

                {/* Floating action button for quick menu */}
                <FloatingActionButton
                    color="primary"
                    onClick={(e) => setQuickMenuAnchor(e.currentTarget)}
                    size="medium"
                >
                    <Icon path={mdiDotsVertical} size={1} />
                </FloatingActionButton>

                <Menu
                    anchorEl={quickMenuAnchor}
                    open={Boolean(quickMenuAnchor)}
                    onClose={() => setQuickMenuAnchor(null)}
                    PaperProps={{
                        sx: {
                            maxHeight: '60vh',
                            overflow: 'auto'
                        }
                    }}
                >
                    {quickActions.map((action, index) => (
                        <MenuItem
                            key={action.label}
                            onClick={() => {
                                action.action();
                                setQuickMenuAnchor(null);
                            }}
                            sx={{ fontSize: '0.875rem' }}
                        >
                            <ListItemIcon>
                                <Icon path={action.icon} size={0.8} />
                            </ListItemIcon>
                            <ListItemText primary={action.label} />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Modals */}
                <MapTypeModal
                    selectedValue={null}
                    open={mapTypeOpen}
                    onClose={handleMapTypeChange}
                />
                {/* ... other modals */}
            </>
        );
    }

    // Desktop layout with progressive disclosure
    return (
        <>
            <ToolbarContainer>
                {/* Map Controls Section */}
                <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <SectionHeader
                        onClick={() => toggleSection('map')}
                        sectionType="map"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon
                                path={mdiMap}
                                size={0.8}
                                className="section-icon"
                                style={{ color: theme.palette.primary.main }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Map Controls
                            </Typography>
                        </Box>
                        <Icon
                            path={expandedSections.map ? mdiChevronUp : mdiChevronDown}
                            size={0.8}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </SectionHeader>
                    <Collapse in={expandedSections.map}>
                        <ActionList>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => setMapTypeOpen(true)}>
                                    <ListItemIcon>
                                        <Icon path={mdiMap} size={0.8} />
                                    </ListItemIcon>
                                    <ListItemText primary="Change Map Type" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => dispatch(zoomIn())}>
                                    <ListItemIcon>
                                        <Icon path={mdiPlus} size={0.8} />
                                    </ListItemIcon>
                                    <ListItemText primary="Zoom In" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => dispatch(zoomOut())}>
                                    <ListItemIcon>
                                        <Icon path={mdiMinus} size={0.8} />
                                    </ListItemIcon>
                                    <ListItemText primary="Zoom Out" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => setFindLatLngOpen(true)}>
                                    <ListItemIcon>
                                        <Icon path={mdiMapMarkerQuestion} size={0.8} />
                                    </ListItemIcon>
                                    <ListItemText primary="Find Coordinates" />
                                </ListItemButton>
                            </ListItem>
                        </ActionList>
                    </Collapse>
                </Paper>

                {/* Layer Controls Section */}
                <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <SectionHeader
                        onClick={() => toggleSection('layers')}
                        sectionType="layers"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon
                                path={mdiLayers}
                                size={0.8}
                                className="section-icon"
                                style={{ color: theme.palette.success.main }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Layers
                            </Typography>
                        </Box>
                        <Icon
                            path={expandedSections.layers ? mdiChevronUp : mdiChevronDown}
                            size={0.8}
                            style={{ color: theme.palette.success.main }}
                        />
                    </SectionHeader>
                    <Collapse in={expandedSections.layers}>
                        <GridContainer>
                            {layerTools.map((tool, index) => (
                                <Tooltip key={tool.label} title={tool.tooltip} placement="top">
                                    <GridButton
                                        onClick={tool.action}
                                        variant={tool.variant}
                                        active={tool.active}
                                        startIcon={<Icon path={tool.icon} size={0.8} />}
                                    >
                                        {tool.label}
                                    </GridButton>
                                </Tooltip>
                            ))}
                        </GridContainer>
                    </Collapse>
                </Paper>

                {isTripViewActive && (
                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <SectionHeader
                            onClick={() => toggleSection('trip')}
                            sectionType="layers"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon
                                    path={mdiMapMarkerPath}
                                    size={0.8}
                                    className="section-icon"
                                    style={{ color: theme.palette.success.main }}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Trip Layers
                                </Typography>
                            </Box>
                            <Icon
                                path={expandedSections.trip ? mdiChevronUp : mdiChevronDown}
                                size={0.8}
                                style={{ color: theme.palette.success.main }}
                            />
                        </SectionHeader>
                        <Collapse in={expandedSections.trip}>
                            <GridContainer>
                                {tripLayerTools.map((tool) => (
                                    <Tooltip key={tool.key} title={tool.tooltip} placement="top">
                                        <GridButton
                                            onClick={tool.action}
                                            active={tool.active}
                                            startIcon={<Icon path={tool.active ? mdiEye : mdiEyeOff} size={0.8} color={tool.color} />}
                                            sx={{
                                                color: tool.active ? tool.color : 'text.secondary',
                                                backgroundColor: tool.active ? `${tool.color}14` : 'transparent',
                                                '&:hover': { backgroundColor: `${tool.color}24` },
                                            }}
                                        >
                                            {tool.label}
                                        </GridButton>
                                    </Tooltip>
                                ))}
                            </GridContainer>
                            <Box sx={{ px: 1.5, pb: 1.5 }}>
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Trip legend
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {[
                                        ['#d95f02', 'Actual truck trail'],
                                        ['#6b7280', 'Planned route'],
                                        ['#228b22', 'Route progress'],
                                    ].map(([color, label]) => (
                                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ width: 14, borderTop: `3px solid ${color}`, borderRadius: 1 }} />
                                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    Arrows indicate travel direction.
                                </Typography>
                            </Box>
                        </Collapse>
                    </Paper>
                )}

                {/* Asset Controls Section - Only show when asset is focused */}
                {focusedAsset && (
                    <Fade in={!!focusedAsset}>
                        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <SectionHeader
                                onClick={() => toggleSection('asset')}
                                sectionType="asset"
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon
                                        path={mdiTruckFast}
                                        size={0.8}
                                        className="section-icon"
                                        style={{ color: theme.palette.warning.main }}
                                    />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        Vehicle Tools
                                    </Typography>
                                </Box>
                                <Icon
                                    path={expandedSections.asset ? mdiChevronUp : mdiChevronDown}
                                    size={0.8}
                                    style={{ color: theme.palette.warning.main }}
                                />
                            </SectionHeader>
                            <Collapse in={expandedSections.asset}>
                                <GridContainer>
                                    {assetTools.map((tool, index) => (
                                        <Tooltip key={tool.label} title={tool.tooltip} placement="top">
                                            <GridButton
                                                onClick={tool.action}
                                                variant={tool.variant}
                                                startIcon={<Icon path={tool.icon} size={0.8} />}
                                            >
                                                {tool.label}
                                            </GridButton>
                                        </Tooltip>
                                    ))}
                                </GridContainer>
                            </Collapse>
                        </Paper>
                    </Fade>
                )}
            </ToolbarContainer>

            {/* Modals */}
            <MapTypeModal
                selectedValue={null}
                open={mapTypeOpen}
                onClose={handleMapTypeChange}
            />
            <AddressModal
                addressOpen={addressOpen}
                handleAddressClose={() => setAddressOpen(false)}
                assetId={focusedAsset}
            />
            <AssetKilometersModal
                distanceOpen={distanceOpen}
                handleDistanceClose={() => setDistanceOpen(false)}
                assetId={focusedAsset}
            />
            <ChangeOdometerModal
                changeOdometerOpen={changeOdometerOpen}
                handleChangeOdometerClose={() => setChangeOdometerOpen(false)}
                assetId={focusedAsset}
            />
            <ChangeIconModal
                changeIconOpen={changeIconOpen}
                handleChangeIconClose={() => setChangeIconOpen(false)}
                assetId={focusedAsset}
            />
            <ShareLinkModal
                shareLinkOpen={shareLinkOpen}
                handleShareLinkClose={() => setShareLinkOpen(false)}
                assetId={focusedAsset}
            />
            {focusedAsset && fuelSetupOpen && (
                <FuelMainModal
                    fuelSetupOpen={fuelSetupOpen}
                    handleFuelSetupClose={() => setFuelSetupOpen(false)}
                    assetId={focusedAsset}
                    userid={user.userid}
                />
            )}
            <FindLatLng
                findLatLngOpen={findLatLngOpen}
                handleFindLatLngClose={() => setFindLatLngOpen(false)}
            />
            <ZoneTagFilterModal
                open={zoneTagFilterOpen}
                handleClose={() => {
                    setZoneTagFilterOpen(false);
                    if (!showZonePolygons) {
                        dispatch(toggleZonePolygons());
                    }
                }}
            />
            <MarkerTagFilterModal
                open={markerTagFilterOpen}
                handleClose={() => {
                    setMarkerTagFilterOpen(false);
                    if (!showPlaceMarkers) {
                        dispatch(togglePlaceMarkers());
                    }
                }}
            />
            <NewTrip
                open={newTripOpen}
                onClose={() => setNewTripOpen(false)}
                initialTruck={{
                    deviceImei: focusedAsset,
                    truckRegistrationNumber: focusedVehicle?.vehicleDevice?.regn || ''
                }}
            />
        </>
    );
};

export default React.memo(MapToolbar);
